import React, { useState, useEffect } from "react";
import CreditCard from "react-credit-cards-2";
import "react-credit-cards-2/dist/es/styles-compiled.css";
import { apiGetAuth, apiPost } from "../utils/Api";
import { ENDPOINTS } from "../utils/EndPoint";
import { useRecoilState } from "recoil";
import { isLoaderState, snakeBarState } from "../store/RecoilStore";
import { Link, useNavigate } from "react-router-dom";
import CustomPrimaryButton from "../components/globals/CustomPrimaryButton";
import CustomModal from "../components/globals/CustomModal";
import { Button, Form } from 'react-bootstrap';
import { IoClose } from "react-icons/io5";

const SavedCards = () => {
    const [savedCards, setSavedCards] = useState([]);
    const [isLoaderInfo, setIsLoaderInfo] = useRecoilState(isLoaderState);
    const [snackBarInfo, setSnackBarInfo] = useRecoilState(snakeBarState);
    const [selectedCard, setSelectedCard] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [focusedField, setFocusedField] = useState("number");
    const navigate = useNavigate();

    const [newCard, setNewCard] = useState({
        cardNumber: "",
        cardholderName: "",
        expiryMonth: "",
        expiryYear: "",
        cvc: "",
    });

    useEffect(() => {
        fetchSavedCards();
    }, []);

    const handleCardChange = (e) => {
        const { name, value } = e.target;
        setNewCard({
            ...newCard,
            [name]: value,
        });
    };

    // Handle card selection and mark it as the default card
    const handleSelectCard = async (cardId) => {
        setIsLoaderInfo(true);

        try {
            const payload = {
                card_id: cardId,  // The ID of the selected card
            };

            await apiPost(ENDPOINTS.SetDefaultCard, payload, (res) => {
                setSnackBarInfo({ message: "Default card updated", type: "success" });
                fetchSavedCards();  // Refresh the list of saved cards
                setSelectedCard(cardId);  // Update the selected card state
            }, (err) => {
                const errorMessage = err?.data?.data || "Failed to update default card";
                setSnackBarInfo({
                    snackStatus: true,
                    snackColor: "bg-danger",
                    snackMsg: errorMessage,
                });
            });

        } catch (error) {
            console.error("Error updating default card:", error);
        } finally {
            setIsLoaderInfo(false);
        }
    };

    const handleDeleteCard = async (cardId) => {
        setIsLoaderInfo(true);

        try {
            const payload = {
                card_id: cardId,
            };

            await apiPost(
                ENDPOINTS.DeleteCard,
                payload,
                (res) => {
                    setSnackBarInfo({ message: "Card deleted successfully", type: "success" });
                    fetchSavedCards();
                }, (err) => {
                    const errorMessage = err?.data?.data || "Failed to delete card";
                    setSnackBarInfo({
                        snackStatus: true,
                        snackColor: "bg-danger",
                        snackMsg: errorMessage,
                    });
                });
        } catch (error) {
            console.error("Error deleting card:", error);
        } finally {
            setIsLoaderInfo(false);
        }
    };

    const fetchSavedCards = async () => {
        setIsLoaderInfo(true);
        apiGetAuth(
            ENDPOINTS.ListCard,
            (res) => {
                setSavedCards(res);
                const defaultCard = res.find(card => card.default_card === 1);
                if (defaultCard) {
                    setSelectedCard(defaultCard.id);
                }

                setIsLoaderInfo(false);
            },
            (err) => {
                console.error("Error fetching saved cards:", err);
                setIsLoaderInfo(false);
            }
        );
    };

    const handleAddCard = async () => {
        const payload = {
            number: newCard.cardNumber,
            cardholder_name: newCard.cardholderName,
            exp_month: newCard.expiryMonth,
            exp_year: newCard.expiryYear,
            cvc: newCard.cvc,
        };

        setIsLoaderInfo(true);
        apiPost(ENDPOINTS.AddCard, payload, (res) => {
            setIsLoaderInfo(false);
            setSnackBarInfo({ message: "Card added successfully", type: "success" });
            setShowModal(false);

            setNewCard({
                cardNumber: "",
                cardholderName: "",
                expiryMonth: "",
                expiryYear: "",
                cvc: "",
            });

            setFocusedField("number");

            fetchSavedCards();
        }, (err) => {
            setIsLoaderInfo(false);

            const errorMessage = err?.data?.data;

            setSnackBarInfo({
                snackStatus: true,
                snackColor: "bg-danger",
                snackMsg: errorMessage,
            });
        });
    };

    return (
        <>
            <div className="container mt-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="skranji-bold">Manage Cards</h3>

                    <CustomPrimaryButton
                        btnTextPro="Add Card"
                        classNamePro=""
                        handleOnClick={() => setShowModal(true)}
                    />
                </div>

                {savedCards.length === 0 ? (
                    <p>No saved cards available.</p>
                ) : (
                    <div className="row">
                        {savedCards.map((card) => (
                            <div key={card.id} className="card shadow-md me-2 d-flex flex-start col-md-3 col-sm-6 mb-4 card-list">
                                <div className="d-flex justify-content-between align-items-center mb-2 mt-2 cursor-pointer">
                                    <IoClose
                                        fontSize={24}
                                        onClick={() => {
                                            setSelectedCard(card.id);
                                            setDeleteModal(true);
                                        }}
                                    />
                                    <div className="d-flex align-items-center">
                                        {card.default_card === 1 && (
                                            <span className="me-2 badge custom-badge text-white rounded-pill py-1 px-2">Default Card</span>
                                        )}
                                        <input
                                            type="radio"
                                            name="selectedCard"
                                            value={card.id}
                                            checked={selectedCard === card.id}
                                            onChange={() => handleSelectCard(card.id)}
                                            className="form-check-input cursor-pointer"
                                            style={{ marginLeft: "auto" }}
                                        />

                                    </div>
                                </div>
                                <div className="mb-4">
                                    <CreditCard
                                        number={card.first4digit + "********" + card.last4digit}
                                        name={card.cardholder_name}
                                        expiry={`${card.card_details.exp_month}/${card.card_details.exp_year}`}
                                        cvc="***"
                                        focused="number"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {/* Add Card Modal */}
            <CustomModal
                size={'lg'}
                hideModal={() => setShowModal(false)}
                showModal={showModal}
                title="Add Card"
                onClose={() => setShowModal(false)}
            >
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 col-md-6 col-sm-12 d-flex align-items-center justify-content-center mb-3">
                            <div className="d-flex align-items-center justify-content-center mb-3">
                                {/* Card Preview */}
                                <CreditCard
                                    number={newCard.cardNumber}
                                    name={newCard.cardholderName}
                                    expiry={`${newCard.expiryMonth}/${newCard.expiryYear}`}
                                    cvc={newCard.cvc}
                                    focused={focusedField}
                                />
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6 col-sm-12">
                            <Form>
                                <Form.Group controlId="cardholderName">
                                    <Form.Label className="font-18 skranji-bold text-white mt-2">Card Holder Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="cardholderName"
                                        value={newCard.cardholderName}
                                        className="font-18"
                                        onChange={handleCardChange}
                                        onFocus={() => setFocusedField("name")}
                                        placeholder="Card Holder Name"
                                    />
                                </Form.Group>

                                <Form.Group controlId="cardNumber">
                                    <Form.Label className="font-18 skranji-bold text-white mt-2">Card Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="cardNumber"
                                        value={newCard.cardNumber}
                                        className="font-18"
                                        onChange={handleCardChange}
                                        onFocus={() => setFocusedField("number")}
                                        placeholder="Enter card number"
                                    />
                                </Form.Group>

                                <Form.Group controlId="cvc">
                                    <Form.Label className="font-18 skranji-bold text-white mt-2">CVC Code</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="cvc"
                                        value={newCard.cvc}
                                        className="font-18"
                                        onChange={handleCardChange}
                                        onFocus={() => setFocusedField("cvc")}
                                        placeholder="Enter CVC"
                                    />
                                </Form.Group>

                                <Form.Group controlId="expiryMonth">
                                    <Form.Label className="font-18 skranji-bold text-white mt-2">Expiry Month</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="expiryMonth"
                                        className="font-18"
                                        value={newCard.expiryMonth}
                                        onChange={handleCardChange}
                                        onFocus={() => setFocusedField("expiryMonth")}
                                        placeholder="MM"
                                    />
                                </Form.Group>

                                <Form.Group controlId="expiryYear">
                                    <Form.Label className="font-18 skranji-bold text-white mt-2">Expiry Year</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="expiryYear"
                                        className="font-18"
                                        value={newCard.expiryYear}
                                        onChange={handleCardChange}
                                        onFocus={() => setFocusedField("expiryYear")}
                                        placeholder="YYYY"
                                    />
                                </Form.Group>
                            </Form>

                            <CustomPrimaryButton
                                btnTextPro="Add Card"
                                classNamePro="w-100 mt-4"
                                handleOnClick={handleAddCard}
                            />
                        </div>
                    </div>
                </div>
            </CustomModal>

            {/* Delete Card Modal */}
            <CustomModal
                size={'sm'}
                hideModal={() => setDeleteModal(false)}
                showModal={deleteModal}
                title="Delete Card"
                onClose={() => setDeleteModal(false)}
            >
                <div className="container text-center">
                    <h5 className="text-white mb-4">Are you sure you want to delete this card?</h5>
                    <p className="text-white">
                        This action cannot be undone.
                    </p>
                    <div className="d-flex justify-content-center mt-4">
                        <button
                            className="btn btn-danger mx-2"
                            onClick={() => {
                                handleDeleteCard(selectedCard);
                                setDeleteModal(false);
                            }}
                        >
                            Confirm
                        </button>
                        <button
                            className="btn btn-secondary mx-2"
                            onClick={() => setDeleteModal(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </CustomModal>
        </>
    );
};

export default SavedCards;
